import { translateOutsideReact } from '@/i18n/client-translator';
import { captureError, getErrorObject } from '@/lib/posthog/captureError';
import { getNotificationById, removeItem, useNotificationStore } from '@/stores/useNotificationStore';
import type { NotificationItem } from '@/types/notification.types';

import { loadSwapNotificationActions } from './load-swap-notification-actions';

const pending = new WeakSet<NotificationItem>();

async function runSwapAction(id: string, action: 'skip' | 'remove'): Promise<void> {
  const item = getNotificationById(id);
  if (!item?.metadata?.swapFlow) {
    if (action === 'remove') removeItem(id);
    return;
  }
  if (pending.has(item)) return;
  pending.add(item);
  const owner = useNotificationStore.getState().vaultPubkey;
  try {
    const actions = await loadSwapNotificationActions();
    // Loading must not let a stale click alter a replacement notification or
    // another wallet's recovery evidence after the active account changes.
    if (useNotificationStore.getState().vaultPubkey !== owner || getNotificationById(id) !== item) return;
    if (action === 'skip') actions.skipPendingSwapNotification(id);
    else {
      actions.dismissRemovedSwapNotification(id);
      removeItem(id);
    }
  } catch (error) {
    captureError(getErrorObject(error), { source: 'NOTIFICATION_SWAP_ACTION' });
    const { customToast } = await import('./toast.api');
    customToast.error(translateOutsideReact('errors.fallback.title'));
  } finally {
    pending.delete(item);
  }
}

export const skipSwapNotification = (id: string | number) => runSwapAction(String(id), 'skip');
export const removeNotificationWithRecovery = (id: string | number) => runSwapAction(String(id), 'remove');
